import React from "react";

export const PrivacyPage: React.FC = () => (
  <div className="privacy-page" style={{ padding: "2.5rem 1.5rem 3.5rem", maxWidth: "960px", margin: "0 auto" }}>
    <h1 style={{ marginBottom: "0.5rem" }}>Privacy Policy</h1>
    <p style={{ marginBottom: "2rem", color: "#475569" }}>Effective Date: January 23, 2026</p>

    <p>
      This Privacy Policy describes how NeuralNets Technologies LLC (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;)
      collects, uses, discloses, and protects information when you access or use our website, applications, and
      related services (collectively, the &quot;Services&quot;). This policy is written primarily for users located
      in the United States.
    </p>
    <p style={{ marginTop: "1rem" }}>
      By using the Services, you agree to the collection and use of information in accordance with this Privacy
      Policy.
    </p>

    <h2 style={{ marginTop: "2.5rem" }}>1. Information We Collect</h2>
    <h3 style={{ marginTop: "1rem" }}>1.1 Information You Provide Directly</h3>
    <p>We collect information that you voluntarily provide to us, which may include:</p>
    <ul>
      <li>Name, email address, or profile information provided through third-party authentication services (e.g., Google, Apple, GitHub)</li>
      <li>Communications you send to us (e.g., support requests, feedback)</li>
      <li>Content you create or upload through the Services (e.g., text, prompts, responses, or other materials)</li>
    </ul>

    <h3 style={{ marginTop: "1rem" }}>1.2 Authentication Information</h3>
    <p>
      We do not store user passwords or credentials. Authentication is handled exclusively through third-party
      identity providers such as Google, Apple, and GitHub. We only retain the minimum reference identifiers and
      metadata necessary to associate your account with these providers.
    </p>

    <h3 style={{ marginTop: "1rem" }}>1.3 Automatically Collected Information</h3>
    <p>When you use the Services, we may automatically collect certain information, including:</p>
    <ul>
      <li>IP address</li>
      <li>Device type, browser type, operating system</li>
      <li>Usage data such as pages visited, features used, timestamps, and interactions</li>
      <li>Approximate location derived from IP address</li>
    </ul>
    <p>
      This information is collected through cookies, web beacons, log files, and similar technologies.
    </p>

    <h2 style={{ marginTop: "2.5rem" }}>2. How We Use Information</h2>
    <h3 style={{ marginTop: "1rem" }}>2.1 AI and Data Processing</h3>
    <p>
      We use generative artificial intelligence and machine learning technologies to provide and improve our
      Services. Information you submit through the Services (such as prompts, inputs, or content) may be processed
      by AI systems to generate responses, insights, or outputs. These systems may be operated by us or by trusted
      third-party AI service providers acting on our behalf.
    </p>
    <p>
      We use this information solely to deliver the requested functionality, improve service quality, enhance
      model performance, and ensure safety and reliability. We do not use user-submitted content to identify
      individuals, and we do not attempt to infer sensitive personal characteristics from such content.
    </p>
    <p>We use the information we collect to:</p>
    <ul>
      <li>Provide, operate, and maintain the Services</li>
      <li>Authenticate users and manage accounts</li>
      <li>Improve functionality, performance, and user experience</li>
      <li>Monitor usage trends and detect security issues or abuse</li>
      <li>Communicate with users regarding updates, features, or support</li>
      <li>Comply with legal obligations</li>
    </ul>
    <p>We do not sell personal information.</p>

    <h2 style={{ marginTop: "2.5rem" }}>3. Cookies and Tracking Technologies</h2>
    <p>We use cookies and similar tracking technologies to:</p>
    <ul>
      <li>Maintain user sessions</li>
      <li>Understand usage patterns</li>
      <li>Improve performance and reliability</li>
    </ul>
    <p>
      You can control or disable cookies through your browser settings; however, some features of the Services may
      not function properly without them.
    </p>

    <h2 style={{ marginTop: "2.5rem" }}>4. Sharing and Disclosure of Information</h2>
    <p>We may share information in the following circumstances:</p>

    <h3 style={{ marginTop: "1rem" }}>4.1 Service Providers</h3>
    <p>We may share information with trusted third-party service providers that perform services on our behalf, such as:</p>
    <ul>
      <li>Cloud hosting and infrastructure providers</li>
      <li>Analytics and monitoring tools</li>
      <li>Customer support platforms</li>
    </ul>
    <p>These providers are authorized to use information only as necessary to provide services to us.</p>

    <h3 style={{ marginTop: "1rem" }}>4.2 Legal and Safety Requirements</h3>
    <p>We may disclose information if required to do so by law or if we believe in good faith that such action is necessary to:</p>
    <ul>
      <li>Comply with a legal obligation</li>
      <li>Protect the rights, property, or safety of users or others</li>
      <li>Prevent fraud, abuse, or security threats</li>
    </ul>

    <h3 style={{ marginTop: "1rem" }}>4.3 Business Transfers</h3>
    <p>
      If we are involved in a merger, acquisition, financing, or sale of assets, user information may be
      transferred as part of that transaction.
    </p>

    <h2 style={{ marginTop: "2.5rem" }}>5. Data Retention</h2>
    <p>
      We retain personal information only for as long as necessary to fulfill the purposes described in this Privacy
      Policy, unless a longer retention period is required or permitted by law.
    </p>

    <h2 style={{ marginTop: "2.5rem" }}>6. Data Security</h2>
    <p>
      We implement reasonable administrative, technical, and organizational measures to protect information against
      unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the
      Internet or electronic storage is completely secure.
    </p>

    <h2 style={{ marginTop: "2.5rem" }}>7. Children&apos;s Privacy</h2>
    <p>
      The Services are not directed to children under the age of 13, and we do not knowingly collect personal
      information from children under 13. If you believe that a child has provided us with personal information,
      please contact us so we can take appropriate action.
    </p>

    <h2 style={{ marginTop: "2.5rem" }}>8. Your Privacy Rights (U.S. Residents)</h2>
    <p>
      Depending on your state of residence, you may have rights regarding your personal information, including the
      right to:
    </p>
    <ul>
      <li>Access the personal information we hold about you</li>
      <li>Request correction or deletion of your information</li>
      <li>Opt out of certain data uses</li>
    </ul>
    <p>To exercise these rights, please contact us using the information below.</p>

    <h2 style={{ marginTop: "2.5rem" }}>9. Third-Party Links and Services</h2>
    <p>
      The Services may contain links to third-party websites or services. We are not responsible for the privacy
      practices of those third parties. We encourage you to review their privacy policies.
    </p>

    <h2 style={{ marginTop: "2.5rem" }}>10. Changes to This Privacy Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated
      effective date. Continued use of the Services after changes become effective constitutes acceptance of the
      revised policy.
    </p>

    <h2 style={{ marginTop: "2.5rem" }}>11. Contact Us</h2>
    <p>If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:</p>
    <p>
      Email: <a href="mailto:support@aiprep.io">support@aiprep.io</a>
    </p>

    <p style={{ marginTop: "2rem", fontStyle: "italic", color: "#64748b" }}>
      This Privacy Policy is provided for general informational purposes and does not constitute legal advice.
    </p>
  </div>
);
